import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { DOMAIN_META, getSubTestDefinition, DOMAIN_MODULE_MAP, isBegrijpendLezenFamily, isTaalverzorgingTaaFamily, type AssessmentDomainId } from '../../../assessment';
import { error as logError } from '../../../utils/logger';
import { genId } from '../../../utils/genId';
import { dataLayer } from '../../../data/dataLayer';
import { Clock, HelpCircle, Users, Calendar, CheckCircle2, Search, ChevronDown, ChevronRight, Check, CheckSquare, UserCheck, Layers } from 'lucide-react';
import { FAMILIES_BY_MODULE, type FamilyDefinition } from '../../../content';
import { DjayAvatar } from '../../../mascot';
import { X } from 'lucide-react';
interface CreateAssessmentPanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3;
function getFamiliesForDomain(domainId: AssessmentDomainId): FamilyDefinition[] {
  const modules = DOMAIN_MODULE_MAP[domainId];
  let families = modules.flatMap(mod => FAMILIES_BY_MODULE.get(mod) || []);
  if (domainId === 'taalverzorging') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isTaalverzorgingTaaFamily(f.familyId));
  } else if (domainId === 'begrijpend-lezen') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isBegrijpendLezenFamily(f.familyId));
  }
  return families;
}
const STEP_CONFIG = [{
  num: 1,
  label: 'Vakgebied'
}, {
  num: 2,
  label: 'Onderdelen'
}, {
  num: 3,
  label: 'Klaarzetten'
}] as const;
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function CreateAssessmentPanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreateAssessmentPanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [selectedDomain, setSelectedDomain] = useState<AssessmentDomainId | null>(null);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hideTip, setHideTip] = useState(false);
  useEffect(() => {
    const pref = localStorage.getItem('mp.teacher.hideAssessmentTip');
    if (pref === 'true') setHideTip(true);
  }, []);
  const dismissTip = () => {
    setHideTip(true);
    localStorage.setItem('mp.teacher.hideAssessmentTip', 'true');
  };
  useEffect(() => {
    if (open) {
      setStep(1);
      setDirection(1);
      setSelectedDomain(null);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
      setShowSuccess(false);
    }
  }, [open]);
  useEffect(() => {
    if (selectedDomain) {
      setSelectedFamilyIds([]);
      setSearchQuery('');
      setExpandedSkills({});
    }
  }, [selectedDomain]);
  const handleNext = () => {
    if (step < 3) {
      setDirection(1);
      setStep(s => s + 1 as Step);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const handleConfirm = async () => {
    if (!selectedDomain) return;
    setIsSubmitting(true);
    const domainDef = getSubTestDefinition(selectedDomain, selectedGroup);
    const questionCount = domainDef?.questionCount || 0;
    const estimatedMinutes = domainDef?.estimatedMinutes || 0;
    const assignmentId = genId('assignment');
    const allFamilies = getFamiliesForDomain(selectedDomain);
    const selectedFams = allFamilies.filter(f => selectedFamilyIds.includes(f.familyId));
    const uniqueSkills = Array.from(new Set(selectedFams.map(f => f.skill.replace(/_/g, ' '))));
    const skillSummary = uniqueSkills.join(', ');
    const wholeClass = selectionMode === 'whole';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      domainId: selectedDomain,
      domainLabel: DOMAIN_META[selectedDomain].label,
      group: selectedGroup,
      questionCount,
      estimatedMinutes,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      selectedFamilyIds: selectedFamilyIds,
      type: 'targeted' as const,
      selectedSkillSummary: skillSummary,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active' as const
    };
    try {
      await dataLayer.saveTeacherAssignment(assignmentId, assignment);
      setShowSuccess(true);
    } catch (error) {
      logError('Failed to save assignment', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const isNextDisabled = () => {
    if (step === 1 && !selectedDomain) return true;
    if (step === 2 && selectedFamilyIds.length === 0) return true;
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  // ── Step indicator ──────────────────────────────────────────────────────
  const renderStepIndicator = () => <div className="flex items-center gap-1 mb-4" data-id="element-7486">
      {STEP_CONFIG.map((s, i) => {
      const isActive = step === s.num;
      const isDone = step > s.num;
      return <Fragment key={s.num} data-id="element-7487">
            {i !== 0 && <div className={`flex-1 h-[2px] rounded-full transition-colors duration-300 ${isDone ? 'bg-orange-500' : 'bg-slate-200'}`} data-id="element-7488" />}
            <div className="flex flex-col items-center gap-1" data-id="element-7489">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isDone ? 'bg-orange-500 text-white' : isActive ? 'bg-orange-500 text-white ring-4 ring-orange-100' : 'bg-slate-100 text-slate-400'}`} data-id="element-7490">
                {isDone ? <Check className="w-3.5 h-3.5" data-id="element-7491" /> : s.num}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isActive ? 'text-orange-600' : isDone ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-7492">
                {s.label}
              </span>
            </div>
          </Fragment>;
    })}
    </div>;
  // ── Running summary strip ───────────────────────────────────────────────
  const renderSummaryStrip = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    const hasDomain = !!selectedDomain;
    const hasContent = selectedFamilyIds.length > 0;
    const hasStudents = selectionMode !== null;
    if (!hasDomain && step === 1) return null;
    return <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-slate-100 pt-3 pb-1" data-id="element-7493">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-7494">
          {hasDomain && meta && Icon && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-7495">
              <div className={`w-4 h-4 rounded flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-7496">
                <Icon className="w-2.5 h-2.5" data-id="element-7497" />
              </div>
              <span className="text-xs font-semibold text-slate-700" data-id="element-7498">
                {meta.shortLabel}
              </span>
            </div>}
          {hasContent && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-7499">
              <Layers className="w-3 h-3 text-slate-500" data-id="element-7500" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-7501">
                {selectedFamilyIds.length} onderdelen
              </span>
            </div>}
          {hasStudents && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-7502">
              <UserCheck className="w-3 h-3 text-slate-500" data-id="element-7503" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-7504">
                {selectionMode === 'whole' ? `${students.length} leerlingen` : `${selectedStudentIds.length} leerling(en)`}
              </span>
            </div>}
          {deadline && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-7505">
              <Calendar className="w-3 h-3 text-slate-500" data-id="element-7506" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-7507">
                {new Date(deadline).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>}
        </div>
      </motion.div>;
  };
  // ── Step 1: Domain ──────────────────────────────────────────────────────
  const renderStep1 = () => <div className="flex flex-col h-full" data-id="element-7508">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-7509">
        <p className="text-sm font-medium text-slate-600" data-id="element-7510">
          Kies het vakgebied voor{' '}
          <span className="font-bold text-slate-900" data-id="element-7511">
            groep {selectedGroup}
          </span>
        </p>
      </div>

      {!hideTip && <div className="flex items-start gap-3 p-3 mb-4 bg-orange-50/60 border border-orange-100 rounded-xl" data-id="element-7512">
          <DjayAvatar size="xs" emotion="pointing" data-id="element-7513" />
          <div className="flex-1 text-xs text-slate-700 mt-0.5 leading-relaxed" data-id="element-7514">
            <strong data-id="element-7515">Een vakgebied</strong> is een groot onderdeel zoals Rekenen
            of Taalverzorging. Kies waarover deze toets moet gaan.
          </div>
          <button onClick={dismissTip} aria-label="Sluit tip" className="text-slate-400 hover:text-slate-600 p-1 -mr-1 -mt-1" data-id="element-7516">
            <X className="w-4 h-4" data-id="element-7517" />
          </button>
        </div>}

      <div className="space-y-2.5 flex-1" data-id="element-7518">
        {domains.map(domainId => {
        const meta = DOMAIN_META[domainId];
        const def = getSubTestDefinition(domainId, selectedGroup);
        const isAvailable = def !== null && def.available;
        const isSelected = selectedDomain === domainId;
        return <div key={domainId} onClick={() => isAvailable && setSelectedDomain(domainId)} className={`p-3.5 rounded-xl border-2 transition-all cursor-pointer ${!isAvailable ? 'opacity-40 border-slate-100 bg-slate-50 cursor-not-allowed' : isSelected ? 'border-orange-500 bg-orange-50/60 shadow-sm' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-7519">
              <div className="flex items-center gap-3.5" data-id="element-7520">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bgColor} ${meta.color}`} data-id="element-7521">
                  <meta.icon className="w-5 h-5" data-id="element-7522" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-7523">
                  <div className="flex items-center justify-between" data-id="element-7524">
                    <h3 className={`font-bold text-[15px] ${isSelected ? 'text-orange-900' : 'text-slate-900'}`} data-id="element-7525">
                      {meta.label}
                    </h3>
                    {isSelected && <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" data-id="element-7526" />}
                  </div>
                  <div className="flex items-center gap-3 mt-1" data-id="element-7527">
                    <span className="text-xs text-slate-500" data-id="element-7528">
                      {meta.description}
                    </span>
                  </div>
                  {isAvailable && def && <div className="flex items-center gap-3 mt-1.5 text-[11px] font-bold text-slate-400" data-id="element-7529">
                      <span className="flex items-center gap-1" data-id="element-7530">
                        <HelpCircle className="w-3 h-3" data-id="element-7531" />
                        {def.questionCount} vr
                      </span>
                      <span className="flex items-center gap-1" data-id="element-7532">
                        <Clock className="w-3 h-3" data-id="element-7533" />~{def.estimatedMinutes} min
                      </span>
                    </div>}
                  {!isAvailable && <span className="mt-1.5 text-[11px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded inline-block" data-id="element-7534">
                      Niet beschikbaar
                    </span>}
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
  // ── Step 2: Family picker ───────────────────────────────────────────────
  const renderStep2 = () => {
    const allFamilies = selectedDomain ? getFamiliesForDomain(selectedDomain) : [];
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const handleSelectAll = () => {
      if (selectedFamilyIds.length === allFamilies.length) {
        setSelectedFamilyIds([]);
      } else {
        setSelectedFamilyIds(allFamilies.map(f => f.familyId));
      }
    };
    const selectedCount = selectedFamilyIds.length;
    const isAllSelected = allFamilies.length > 0 && selectedCount === allFamilies.length;
    return <div className="flex flex-col h-full" data-id="element-7535">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-7536">
          <p className="text-sm font-medium text-slate-600" data-id="element-7537">
            Welke onderdelen wil je toetsen?
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-2" data-id="element-7538">
          <div className="flex items-center gap-2" data-id="element-7539">
            <span className="text-sm font-bold text-orange-600" data-id="element-7540">
              {selectedCount}
            </span>
            <span className="text-xs text-slate-500" data-id="element-7541">
              / {allFamilies.length} geselecteerd
            </span>
          </div>
          <button onClick={handleSelectAll} className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 px-2 py-1 rounded-lg hover:bg-orange-50 transition-colors" data-id="element-7542">
            <CheckSquare className="w-3.5 h-3.5" data-id="element-7543" />
            {isAllSelected ? 'Deselecteer' : 'Alles'}
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3" data-id="element-7544">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-7545" />
          <Input placeholder="Zoek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white h-10 text-sm" data-id="element-7546" />
        </div>

        {/* Scrollable skill groups */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 pb-2" data-id="element-7547">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSkillSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-7548">
                <div className="flex items-center justify-between min-h-[44px] px-3" data-id="element-7549">
                  <div className="flex items-center gap-2 cursor-pointer flex-1 py-2" onClick={() => toggleExpand(skill)} data-id="element-7550">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-7551" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-7552" />}
                    <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-7553">
                      {skill.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isAllSkillSelected ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-7554">
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-2 -mr-1" onClick={() => toggleSkillGroup(families)} data-id="element-7555">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAllSkillSelected ? 'bg-orange-500 border-orange-500 text-white' : isPartiallySelected ? 'bg-orange-100 border-orange-400 text-orange-500' : 'border-slate-300 bg-white'}`} data-id="element-7556">
                      {isAllSkillSelected && <Check className="w-3 h-3" data-id="element-7557" />}
                      {isPartiallySelected && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-7558" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="border-t border-slate-100" data-id="element-7559">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <div key={f.familyId} onClick={() => toggleFamily(f.familyId)} className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-b-0" data-id="element-7560">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-7561">
                            {isSelected && <Check className="w-3 h-3" data-id="element-7562" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-7563">
                            <span className={`text-sm truncate block ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-7564">
                              {f.description}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase flex-shrink-0" data-id="element-7565">
                            {f.levelCode}
                          </span>
                        </div>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-7566">
              <div className="mb-3 flex justify-center" data-id="element-7567">
                <DjayAvatar size="md" emotion="thinking" data-id="element-7568" />
              </div>
              Geen resultaten voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  // ── Step 3: Students + Deadline + Summary ───────────────────────────────
  const renderStep3 = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    return <div className="flex flex-col gap-4 pb-4" data-id="element-7569">
        <div className="flex items-center gap-2.5" data-id="element-7570">
          <p className="text-sm font-medium text-slate-600" data-id="element-7571">
            Bijna klaar! Kies de leerlingen.
          </p>
        </div>

        {/* Student selection */}
        <div className="grid grid-cols-2 gap-2" data-id="element-7572">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'whole' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-7573">
            <Users className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'whole' ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-7574" />
            <div className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-7575">
              Hele klas
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-7576">
              {students.length} leerlingen
            </div>
          </div>
          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'specific' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-7577">
            <UserCheck className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'specific' ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-7578" />
            <div className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-7579">
              Selectie
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-7580">Kies leerlingen</div>
          </div>
        </div>

        {/* Student list (only when specific) */}
        {selectionMode === 'specific' && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-7581">
            <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-600 border-b border-slate-200" data-id="element-7582">
              {selectedStudentIds.length} van {students.length} geselecteerd
            </div>
            <div className="max-h-[200px] overflow-y-auto divide-y divide-slate-100" data-id="element-7583">
              {students.map(student => {
            const isSelected = selectedStudentIds.includes(student.id);
            return <div key={student.id} onClick={() => toggleStudent(student.id)} className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors ${isSelected ? 'bg-orange-50/40' : 'hover:bg-slate-50'}`} data-id="element-7584">
                    <div className="flex items-center gap-2.5" data-id="element-7585">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-7586">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800" data-id="element-7587">
                        {student.name}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-7588">
                      {isSelected && <Check className="w-3 h-3" data-id="element-7589" />}
                    </div>
                  </div>;
          })}
            </div>
          </div>}

        {/* Deadline */}
        <div className="flex items-center gap-3" data-id="element-7590">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-7591" />
          <div className="flex-1" data-id="element-7592">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-7593">
              Deadline (optioneel)
            </label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="h-9 text-sm" min={new Date().toISOString().split('T')[0]} data-id="element-7594" />
          </div>
        </div>

        {/* Summary card */}
        <Card className="p-3.5 border-orange-200 bg-orange-50/40" data-id="element-7595">
          <div className="flex items-center gap-3" data-id="element-7596">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta?.bgColor} ${meta?.color}`} data-id="element-7597">
              {Icon && <Icon className="w-5 h-5" data-id="element-7598" />}
            </div>
            <div className="flex-1 min-w-0" data-id="element-7599">
              <div className="font-bold text-slate-900 text-sm" data-id="element-7600">
                {meta?.label}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-7601">
                {selectedFamilyIds.length} onderdelen
                <span className="text-slate-300 mx-1.5" data-id="element-7602">·</span>
                {selectionMode === 'whole' ? `${students.length} leerlingen` : selectionMode === 'specific' ? `${selectedStudentIds.length} leerling(en)` : 'Nog niet gekozen'}
                {deadline && <>
                    <span className="text-slate-300 mx-1.5" data-id="element-7603">·</span>
                    tot{' '}
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'short'
                })}
                  </>}
              </div>
            </div>
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${!isNextDisabled() ? 'text-emerald-500' : 'text-slate-300'}`} data-id="element-7604" />
          </div>
        </Card>
      </div>;
  };
  return <SlideOver open={open} onClose={onClose} title="Toets Klaarzetten" description={<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-orange-100 text-orange-700" data-id="element-7606">
          Groep {selectedGroup}
        </span>} width="lg" data-id="element-7605">
      <div className="flex flex-col h-full" data-id="element-7607">
        {showSuccess ? <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-7608">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} className="mb-6" data-id="element-7609">
              <DjayAvatar size="lg" emotion="proud" data-id="element-7610" />
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" data-id="element-7611">
              <CheckCircle2 className="w-8 h-8" data-id="element-7612" />
            </motion.div>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-2xl font-black text-slate-900 mb-2" data-id="element-7613">
              Toets klaargezet! 🎉
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-slate-600 mb-6 max-w-sm" data-id="element-7614">
              De toets{' '}
              <span className="font-bold" data-id="element-7615">
                {selectedDomain ? DOMAIN_META[selectedDomain].label : ''}
              </span>{' '}
              voor{' '}
              <span className="font-bold" data-id="element-7616">
                {selectionMode === 'whole' ? 'de hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </span>{' '}
              is klaargezet.
              {deadline && <>
                  {' '}
                  Deadline:{' '}
                  <span className="font-bold" data-id="element-7617">
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long'
              })}
                  </span>
                  .
                </>}
            </motion.p>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-8 max-w-sm w-full" data-id="element-7618">
              <p className="text-sm text-slate-500" data-id="element-7619">
                Je kunt deze toets aanpassen of verwijderen op de
                Toetsen-pagina.
              </p>
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-7620">
              <Button onClick={() => {
            onSuccess();
            onClose();
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 font-semibold" data-id="element-7621">
                Sluiten
              </Button>
            </motion.div>
          </div> : <>
            {/* Step indicator */}
            {renderStepIndicator()}

            {/* Step content with animation */}
            <div className="flex-1 overflow-hidden relative" data-id="element-7622">
              <AnimatePresence mode="wait" custom={direction} data-id="element-7623">
                <motion.div key={step} custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 overflow-y-auto px-0.5" data-id="element-7624">
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Running summary + actions */}
            <div className="flex-shrink-0 bg-white" data-id="element-7625">
              {renderSummaryStrip()}

              <div className="flex items-center justify-between pt-3 pb-safe" data-id="element-7626">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-11 px-4" data-id="element-7627">
                  {step === 1 ? 'Annuleren' : 'Terug'}
                </Button>

                {step < 3 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-7628">
                    Volgende
                    <ChevronRight className="w-4 h-4 ml-1" data-id="element-7629" />
                  </Button> : <Button onClick={handleConfirm} disabled={isSubmitting || isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-7630">
                    {isSubmitting ? 'Bezig...' : 'Klaarzetten'}
                  </Button>}
              </div>
            </div>
          </>}
      </div>
    </SlideOver>;
}